/* eslint-disable react/prop-types */
import classNames from "classnames";
import React from "react";
import { Form } from "react-bootstrap";
import { useEffect } from "react";
import { useTable, useSortBy, usePagination, useRowSelect, useGlobalFilter } from "react-table";

export const IndeterminateCheckbox = React.forwardRef(({ indeterminate, className, ...rest }, ref) => {
	const defaultRef = React.useRef();

	const resolvedRef = ref || defaultRef;

	React.useEffect(() => {
		resolvedRef.current.indeterminate = indeterminate;
	}, [resolvedRef, indeterminate]);

	return (
		<Form.Check type="checkbox" className={classNames("form-check fs-0 mb-0", className)}>
			<Form.Check.Input type="checkbox" ref={resolvedRef} {...rest} />
		</Form.Check>
	);
});

const AdvanceTableWrapper = ({ 
	children,
	columns,
	data,
	sortable, 
	selection, 
	selectionColumnWidth, 
	pagination, 
	perPage = 10,
	onSort,
    initialSortBy = [],
	sortDescFirst = true,
    defaultSortField = 'modificationDate',
	defaultSortDirection = 'desc'
  }) => {

	// Modify the columns to set their initial sortDirection property
  const columnsWithSortDesc = React.useMemo(() => {
    if (sortable && sortDescFirst) {
      return columns.map(column => ({
        ...column,
        disableSortBy: column.disableSortBy,
        // Set initial sort direction to descending
        sortDescFirst: true
      }));
    }
    return columns;
  }, [columns, sortable, sortDescFirst]);

	const {
		getTableProps,
		headers,
		page,
		prepareRow,
		canPreviousPage,
		canNextPage,
		nextPage,
		previousPage,
		setPageSize,
		gotoPage,
		pageCount,
		state: { pageIndex, pageSize, selectedRowIds, globalFilter, sortBy  },
		setGlobalFilter,
	} = useTable(
		{
			columns: columnsWithSortDesc,
			data,
			disableSortBy: !sortable,
			initialState: { 
                pageSize: pagination ? perPage : data.length,
                sortBy: initialSortBy
            },
            manualSortBy: Boolean(onSort), // Tell table we'll handle sorting manually
            autoResetSortBy: false,
		},
		useGlobalFilter,
		useSortBy,
		usePagination,
		useRowSelect,
		(hooks) => {
			if (selection) {
				hooks.visibleColumns.push((columns) => [
					{
						id: "selection",
						Header: ({ getToggleAllRowsSelectedProps }) => <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />,
						headerProps: {
							style: {
								width: selectionColumnWidth,
							},
						},
						cellProps: {
							style: {
								width: selectionColumnWidth,
							},
						},
						Cell: ({ row }) => (
							<div>
								<IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
							</div>
						),
					},
					...columns,
				]);
			}
		}
	);

	const initialRenderRef = React.useRef(true);
    const prevSortByRef = React.useRef(initialSortBy);
    
    useEffect(() => {
        // Skip the effect on the initial render
        if (initialRenderRef.current) {
            initialRenderRef.current = false;
            return;
        }

	    if (onSort && sortBy.length === 0) {
			// Reset to default sort
			onSort(defaultSortField, defaultSortDirection);
			return;
		}
        
        // Only trigger onSort if sortBy has actually changed
        if (onSort && sortBy.length > 0) {
            const { id, desc } = sortBy[0];
            
            // Check if sort state has actually changed
            const prevSort = prevSortByRef.current;
            const prevId = prevSort.length > 0 ? prevSort[0].id : null;
            const prevDesc = prevSort.length > 0 ? prevSort[0].desc : null;
            
            if (id !== prevId || desc !== prevDesc) {
                // Update the ref to the current sort state
                prevSortByRef.current = sortBy;
                // Call the sort handler
                onSort(id, desc ? 'desc' : 'asc');
            }
        }
    }, [sortBy, onSort, defaultSortField, defaultSortDirection]);

	const recursiveMap = (children) => {
		return React.Children.map(children, (child) => {
			if (child.props?.children) {
				return React.cloneElement(child, {
					children: recursiveMap(child.props.children),
				});
			} else {
				if (child.props?.table) {
					return React.cloneElement(child, {
						...child.props,
						getTableProps,
						headers,
						page,
						prepareRow,
						canPreviousPage,
						canNextPage,
						nextPage,
						previousPage,
						gotoPage,
						pageCount,
						pageIndex,
						selectedRowIds,
						pageSize,
						setPageSize,
						globalFilter,
						setGlobalFilter,
					});
				} else {
					return child;
				}
			}
		});
	};

	return (
		<>{recursiveMap(children)}</>
	);
};

export default AdvanceTableWrapper;
